"use client"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import Script from "next/script"
import { useEffect, useRef } from "react"
import { extractUserData } from "../utils/tracking/klaviyoForms"

if (typeof document !== "undefined") {
    const cookieConsent = document.cookie
        .split("; ")
        .find(c => c.startsWith("CookieConsent="))

    const hasMarketingConsent =
        !!cookieConsent &&
        /marketing\s*:\s*true/.test(
            decodeURIComponent(cookieConsent.split("=").slice(1).join("="))
        )

    if (!hasMarketingConsent) {
        document.cookie = "__kla_off=true; path=/; max-age=31536000; samesite=lax; Secure"
    }
}

export const KlaviyoScript = () => {
    const eventHandlerRef = useRef<((e: any) => void) | null>(null)
    const siteContext = useSiteContext()
    const siteContextRef = useRef(siteContext)
    const lastEventRef = useRef<{ type: string; timestamp: number } | null>(null)

    useEffect(() => {
        siteContextRef.current = siteContext
    }, [siteContext])

    useEffect(() => {
        const eventHandler = (e: any) => {
            const now = Date.now()

            // Prevent duplicate events within 100ms
            if (
                lastEventRef.current &&
                lastEventRef.current.type === e.detail.type &&
                now - lastEventRef.current.timestamp < 100
            ) {
                return
            }

            lastEventRef.current = { type: e.detail.type, timestamp: now }

            if (e.detail.type === "open") {
                import("@/lib/utils/tracking/gtmEvents").then(({ modalEvent }) => {
                    modalEvent(
                        "klaviyo-popup",
                        "Become a POC Member",
                        "open",
                        siteContextRef.current
                    )
                })
            } else if (e.detail.type === "close") {
                import("@/lib/utils/tracking/gtmEvents").then(({ modalEvent }) => {
                    modalEvent(
                        "klaviyo-popup",
                        "Become a POC Member",
                        "close",
                        siteContextRef.current
                    )
                })
            } else if (e.detail.type === "submit") {
                const determineSignupMethod = (detail: any, pathname: string): string => {
                    if (pathname.includes("/checkout")) {
                        return "Checkout"
                    }

                    if (detail.formId || detail.formType === "popup") {
                        return "Modal"
                    }

                    if (pathname === "/" || pathname.endsWith("/")) {
                        return "Footer"
                    }

                    return "Newsletter"
                }

                const userData = extractUserData(e.detail)
                const signupMethod = determineSignupMethod(
                    e.detail,
                    window.location.pathname
                )

                if (userData.email) {
                    import("@/lib/utils/tracking/gtmEvents").then(({ signUpEvent }) => {
                        signUpEvent(signupMethod, siteContextRef.current, userData)
                    })
                }
            }
        }

        eventHandlerRef.current = eventHandler
        window.addEventListener("klaviyoForms", eventHandler)

        return () => {
            if (eventHandlerRef.current) {
                window.removeEventListener("klaviyoForms", eventHandlerRef.current)
                eventHandlerRef.current = null
            }
        }
    }, [])

    return (
        <Script
            strategy="lazyOnload"
            src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.NEXT_PUBLIC_KLAVIYO_ID}`}
            id={`klaviyo-script-${process.env.NEXT_PUBLIC_KLAVIYO_ID}`}
        />
    )
}
